











































































































import { defineComponent, PropType, computed, reactive, onMounted, ref, watch, toRefs } from '@vue/composition-api';
import Vue from 'vue';
import { UTM, FullNormalFormData, AcoFormData, AdgroupFormData, CampaignFormData } from '@tt4b/ttam-enums';
import ALink from '@/components/common/a-link/a-link.vue';
import { i18n } from '@tt4b/ttam-i18n';
import { parseUrl } from 'query-string';
import TtamCreativeUtmDialogFooter from './footer.vue';
import TtamCreativeUtmParamRow from './param-row.vue';
import { useFooterShow } from './hooks';
import { useIsNotSupportProductLink, useStdAdgroupIs, useAllowlist } from '@tt4b/ttam-hooks';

const name = 'ttam-creative-utm-dialog';

export default defineComponent({
  name,
  components: {
    TtamCreativeUtmDialogFooter,
    TtamCreativeUtmParamRow,
    ALink,
  },
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: '',
    },
    // 是否打开了dynamic：影响弹窗顶部dynamic提示、底部param区展示
    isDlp: {
      type: Boolean,
      default: false,
    },
    isOpenUrl: {
      type: Boolean,
      default: false,
    },
    isVsa: {
      type: Boolean,
      default: true,
    },
    isLegacy: {
      type: Boolean,
      default: false,
    },
    isEntryNewUI: {
      type: Boolean,
      default: false,
    },
    originalQuery: {
      type: Array,
      default: () => [],
    },
    isProductDeeplink: {
      type: Boolean,
      default: false,
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const { creativeData, campaignData, adgroupData } = toRefs(props);
    const { isUtmBuilderWhite } = useAllowlist();
    const utms = computed(() => (props.isOpenUrl ? creativeData.value.openUrlUtms : creativeData.value.utms));
    const urlType = computed(() => creativeData.value.destinationUrlType);
    const paramsType = computed(() =>
      props.isOpenUrl ? creativeData.value.dpaOpenUrlType : creativeData.value.paramsType,
    );
    const { isStandardAdsWithCatalog } = useStdAdgroupIs({
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
    });
    // UTM参数对象
    const utmParams = reactive({
      utm_source: '',
      utm_medium: '',
      utm_content: '',
      utm_campaign: '',
    });
    // custom参数列表
    const customParams: UTM[] = reactive([]);
    const showDedupTips = ref(false);
    const pureCustomParams = ref('');
    const isDisableAddCustomParam = ref(false);
    const formRef = ref();
    const originParam = ref([]);

    const helpLink = 'https://ads.tiktok.com/help/preview?content_id=3Gfz8OwV9SCHZvOnvYeWZN';

    const customParamModel = ref({});
    const formModel = computed(() => ({
      ...utmParams,
      ...customParamModel.value,
    }));

    watch(
      customParams,
      () => {
        // 更新custom参数字符串
        pureCustomParams.value = customParams
          .filter(({ field, value }) => Boolean(field) && Boolean(value))
          .map(({ field, value }) => `${encodeURIComponent(field)}=${encodeURIComponent(value)}`)
          .join('&');

        const customParamLimit = 10;
        isDisableAddCustomParam.value = customParams.length >= customParamLimit;

        customParamModel.value = customParams.reduce((obj, cur, index) => {
          obj[`label__${index}`] = cur.field;
          obj[`value__${index}`] = cur.value;
          return obj;
        }, {});
      },
      { deep: true },
    );
    const { isNotSupportProductLink: onlySupportCustomLink } = useIsNotSupportProductLink({
      creativeData,
      adgroupData,
    });
    const { showLabel, showCustomLink, showProductLink } = useFooterShow({
      isDlp: props.isDlp,
      urlType: urlType.value,
      paramsType: paramsType.value,
      isOpenUrl: props.isOpenUrl,
      isVsa: props.isVsa,
      isEntryNewUI: props.isEntryNewUI,
    });
    const headerTip = computed(() => {
      if (showCustomLink.value && showProductLink.value) {
        return i18n.t('creative_utm_product_tip_and_url_tip');
      } else if (showCustomLink.value || showLabel.value) {
        // return i18n.t('creative_utm_custom_url_tip');
        // Whitelist on English only, wait until starling key ready after release.
        return 'We will apply UTM parameters to your website URL.';
      } else {
        return i18n.t('creative_utm_product_tip');
      }
    });
    const pureUrl = computed(() => {
      const { url } = parseUrl(props.url.trim());
      return url || '-';
    });

    const pureParams = computed(() => {
      const pureUtmParams = Object.entries(utmParams)
        .filter(([field, value]) => Boolean(value))
        .map(([field, value]) => `${encodeURIComponent(field)}=${encodeURIComponent(value)}`)
        .join('&');
      if (!pureUtmParams && !pureCustomParams.value) {
        return '';
      }
      return `?${[pureUtmParams, pureCustomParams.value].filter(str => Boolean(str)).join('&')}`;
    });

    const paramsArray = computed(() => {
      if (pureParams.value.length === 0) {
        return [];
      }

      return pureParams.value
        .substring(1)
        .split('&')
        .map(param => `&${param}`);
    });

    const showOriginalUrl = computed(() => {
      const { query } = parseUrl(props.url.trim());
      const isQueryExist = Boolean(
        Object.entries(query).filter(([field, value]) => Boolean(field) && Boolean(value)).length,
      );
      if (props.isVsa) {
        return !isUtmBuilderWhite.value && props.isLegacy && isQueryExist;
      } else {
        return false;
      }
    });

    const onClose = () => {
      emit('close');
    };

    const onApply = () => {
      formRef.value.validate(valid => {
        if (valid) {
          emit('apply', pureParams.value);
          emit('close');
        }
      });
    };

    const checkDedupFields = () => {
      const utmFields = Object.entries(utmParams)
        .filter(([field, value]) => Boolean(value))
        .map(([field]) => field);
      const customFields = customParams
        .filter(({ field, value }) => Boolean(field) && Boolean(value))
        .map(({ field }) => field);

      showDedupTips.value = utmFields.concat(customFields).length > new Set([...utmFields, ...customFields]).size;
    };

    const handleUtmValueChange = (value, field) => {
      utmParams[field] = value;
      checkDedupFields();
    };

    const handleCustomFieldChange = (newField, index) => {
      Vue.set(customParams, index, {
        field: newField,
        value: customParams[index].value,
      });
      checkDedupFields();
    };

    const handleCustomValueChange = (value, index) => {
      Vue.set(customParams, index, {
        field: customParams[index].field,
        value,
      });
      checkDedupFields();
    };

    const handleCustomParamDelete = index => {
      customParams.splice(index, 1);
      checkDedupFields();
    };

    const addCustomParam = () => {
      customParams.push({
        field: '',
        value: '',
      });
    };

    const getInitParam = () => {
      if (utms.value.length) {
        return utms.value;
      }
      // 当utms参数没有时，从url中截取下来回填
      if (props.originalQuery.length > 0) {
        originParam.value = props.originalQuery.map(obj => obj?.field);
      }
      const { query } = parseUrl(props.url, { decode: false });
      return Object.entries(query)
        .filter(([field, value]) => Boolean(field) && Boolean(value) && !originParam.value.includes(field))
        .map(([field, value]) => ({ field, value }));
    };

    const initializeData = () => {
      const utmKeys = Object.keys(utmParams);
      getInitParam()
        .filter(({ field, value }) => Boolean(field) && Boolean(value))
        .forEach(({ field, value }) => {
          // 是utm的参数
          if (utmKeys.includes(field)) {
            if (utmParams[field] === '') {
              utmParams[field] = decodeURIComponent(value);
              return;
            }
            // 预防有重复的utm参数，就把重复的utm参数，放在custom的区域
            customParams.push({
              field: decodeURIComponent(field),
              value: decodeURIComponent(value),
            });
          } else {
            customParams.push({
              field: decodeURIComponent(field),
              value: decodeURIComponent(value),
            });
          }
        });
    };

    onMounted(() => {
      // 回填参数对象（utmParams、customParams）
      initializeData();
      checkDedupFields();
    });

    return {
      name,
      headerTip,
      onClose,
      onApply,
      formRef,
      pureUrl,
      pureParams,
      paramsArray,
      showOriginalUrl,
      utmParams,
      customParams,
      formModel,
      initializeData,
      isDisableAddCustomParam,
      isUtmBuilderWhite,
      showDedupTips,
      onlySupportCustomLink,
      addCustomParam,
      checkDedupFields,
      handleUtmValueChange,
      handleCustomFieldChange,
      handleCustomValueChange,
      handleCustomParamDelete,
      showLabel,
      showCustomLink,
      showProductLink,
      helpLink,
      getInitParam,
    };
  },
});
